<template>
  <div class="ir-form">
    <v-expansion-panels v-model="openPanels" multiple>
      <!-- Reserved Name section: custom rendering with v-select -->
      <v-expansion-panel value="reservedName">
        <v-expansion-panel-title class="bg-primary text-white">
          <div class="d-flex align-center gap-2">
            <v-icon size="20" color="white">mdi-bookmark-check</v-icon>
            <span class="font-weight-medium">{{ tl('1. Reserved Name') }}</span>
            <v-chip
              v-if="sectionErrors['reservedName']?.length"
              size="x-small"
              color="error"
              variant="flat"
            >
              {{ sectionErrors['reservedName'].length }} {{ tl('error(s)') }}
            </v-chip>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-select
            v-model="selectedEntityName"
            :items="reservationSelectItems"
            item-title="text"
            item-value="value"
            :label="tl('Reserved Entity Name')"
            :loading="loadingReservations"
            :disabled="readOnly || (!loadingReservations && reservationSelectItems.length === 0)"
            :readonly="readOnly"
            :no-data-text="loadingReservations ? tl('Loading...') : tl('No approved reservations found')"
            variant="outlined"
            density="compact"
            class="mt-2"
            :error-messages="sectionErrors['reservedName']"
            @update:model-value="handleReservationSelectChange"
          />
          <v-alert
            v-if="!loadingReservations && reservationSelectItems.length === 0"
            type="warning"
            variant="tonal"
            density="compact"
          >
            {{ tl('No Approved Reservations Detail') }}
          </v-alert>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Other sections: rendered by SectionRenderer -->
      <v-expansion-panel
        v-for="section in filteredSections"
        :key="section.id"
        :value="section.id"
      >
        <v-expansion-panel-title class="bg-primary text-white">
          <div class="d-flex align-center gap-2">
            <v-icon size="20" color="white">
              {{ getSectionIcon(section) }}
            </v-icon>
            <span class="font-weight-medium">{{ tl(section.title) }}</span>
            <v-chip
              v-if="sectionErrors[section.id]?.length"
              size="x-small"
              color="error"
              variant="flat"
            >
              {{ sectionErrors[section.id].length }} {{ tl('error(s)') }}
            </v-chip>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <SectionRenderer
            :ref="(el: any) => setSectionRef(section.id, el)"
            :section="section"
            :form-data="formData"
            :readonly="readOnly"
            :ref-data="refData"
            :resolve-template-fn="resolveTemplate"
            :resolve-group-fn="resolveGroup"
            :show-errors="showAddressErrors"
            :contacts="contacts"
            :contacts-loading="isLoadingContacts"
            @update:field="handleFieldUpdate"
            @update:address="handleAddressUpdate"
            @update:list="handleListUpdate"
            @update:dynamicList="handleDynamicListUpdate"
            @member-toggle="handleDirectorMemberToggle"
            @contact-change="handleContactChange"
            @date-picker-change="handleDatePickerChange"
            @date-blur="handleDateBlur"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import SectionRenderer from '../shared/renderers/SectionRenderer.vue';
import { useFormConfig } from './composables/useFormConfig';
import type { SectionDef, RefDataItem } from './composables/useFormConfig';
import type { ReservationItem, ReservationDirectorItem } from '../../api/incorporateReservationApi';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Props
interface Props {
  taskConfig?: any;
  taskId?: string;
  loading?: boolean;
  readOnly?: boolean;
  reservationList?: ReservationItem[];
  loadingReservations?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  taskId: undefined,
  loading: false,
  readOnly: false,
  reservationList: () => [],
  loadingReservations: false,
});

// Emits
const emit = defineEmits<{
  'update:formData': [data: any];
  'reservation-select': [item: ReservationItem];
}>();

// Form config
const { sections, refData, resolveTemplate, resolveGroup } = useFormConfig();

// Section refs
const sectionRefs: Record<string, any> = {};
function setSectionRef(id: string, el: any) {
  if (el) sectionRefs[id] = el;
}

// Panels
const openPanels = ref<string[]>(sections.map(s => s.id));

// Form data
const formData: Record<string, any> = reactive({});

// Dynamic lists
const directors = ref<Record<string, any>[]>([]);
const shareholders = ref<Record<string, any>[]>([]);
const businessCodes = ref<string[]>(['']);

// Reservation select
const selectedEntityName = ref<string>('');
const reservationSelectItems = computed(() =>
  (props.reservationList || []).map(r => ({
    text: r.entity_name + (r.reservation_number ? ` (${r.reservation_number})` : ''),
    value: r.entity_name,
  }))
);
const filteredSections = computed(() => sections.filter(s => s.id !== 'reservedName'));

// Auto-select first reservation when list is loaded
watch(() => props.reservationList, (newList) => {
  if (newList && newList.length > 0 && !selectedEntityName.value) {
    const first = newList[0];
    selectedEntityName.value = first.entity_name;
    handleReservationSelectChange(first.entity_name);
  }
});

// Validation
const sectionErrors = ref<Record<string, string[]>>({});
const showAddressErrors = ref(false);

// Contact list
interface Contact {
  contactCd: string;
  contactName: string;
  contactRegNo: string;
  clientCd: string;
  categoryCd: string;
  displayName: string;
  contactEmail: string;
  dateOfBirth?: string | null;
  idTypeCd?: string;
  idTypeName?: string;
}
const contacts = ref<Contact[]>([]);
const isLoadingContacts = ref(false);

const loadContacts = async () => {
  isLoadingContacts.value = true;
  try {
    const response = await apiClient.get('/startTask/common/officers');
    const data = (response as any)?.data || response;
    contacts.value = data?.officers || [];
  } catch (error) {
    console.error('[IncorporateReservationForm] Failed to load contacts:', error);
    contacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
};

// Date format
const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

function formatDateToDisplay(date: Date): string {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function parseDateString(value: string): Date | null {
  const displayMatch = value.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (displayMatch) {
    const monthIdx = MONTH_NAMES.indexOf(displayMatch[2]);
    if (monthIdx >= 0) return new Date(parseInt(displayMatch[3]), monthIdx, parseInt(displayMatch[1]));
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }
  return null;
}

// ID type mapping
const idTypeMapping: Record<string, Record<string, string>> = {
  byName: { 'NRIC': 'NRIC', 'MALAYSIAN NRIC': 'NRIC', 'PASSPORT': 'PASSPORT', 'ARMY ID': 'ARMY_ID', 'POLICE ID': 'POLICE_ID', 'MYKAS': 'MYKAS', 'PERMANENT RESIDENCE NUMBER': 'PR_NUMBER' },
  byCode: { 'M01002': 'ARMY_ID', 'M01003': 'MYKAS', 'M01004': 'NRIC', 'M01005': 'PASSPORT', 'M01006': 'POLICE_ID', 'M01007': 'PR_NUMBER' },
  byDisplay: { 'NRIC': 'NRIC', 'IC': 'NRIC', 'PASSPORT': 'PASSPORT', 'PASS': 'PASSPORT', 'PR': 'PR_NUMBER', 'ARMY': 'ARMY_ID', 'POLICE': 'POLICE_ID', 'MYKAS': 'MYKAS' },
};

function mapContactIdType(contact: Contact): string {
  if (contact.idTypeName) {
    const upper = contact.idTypeName.toUpperCase().trim();
    if (idTypeMapping.byName[upper]) return idTypeMapping.byName[upper];
    for (const [k, v] of Object.entries(idTypeMapping.byDisplay)) {
      if (upper.includes(k)) return v;
    }
  }
  if (contact.idTypeCd) {
    return idTypeMapping.byCode[contact.idTypeCd.toUpperCase().trim()] || '';
  }
  return '';
}

// Initialize
onMounted(async () => {
  // Initialize defaults from JSON config
  for (const section of sections) {
    if (section.fields) {
      for (const field of section.fields) {
        if (field.default !== undefined) {
          const key = `${section.id}.${field.id}`;
          if (formData[key] === undefined) {
            formData[key] = field.default;
          }
        }
      }
    }
    if (section.conditionalGroups) {
      for (const cg of section.conditionalGroups) {
        for (const field of cg.fields) {
          if (field.default !== undefined) {
            const key = `${section.id}.${field.id}`;
            if (formData[key] === undefined) {
              formData[key] = field.default;
            }
          }
        }
      }
    }
  }

  await loadContacts();
});

// Shareholder type detection
function determineShareholderType(contact: Contact): 'Individual' | 'BodyCorporate' {
  if (contact.categoryCd === 'B09002' || contact.displayName) return 'Individual';
  if (contact.contactRegNo && /^[0-9]{6,}[A-Z]?$/.test(contact.contactRegNo)) {
    if (contact.contactRegNo.length >= 6 && contact.contactRegNo.length <= 10) return 'BodyCorporate';
  }
  return contact.displayName ? 'Individual' : 'BodyCorporate';
}

// Contact auto-fill
// nextTick is required: FieldRenderer emits contact-change first, then update:modelValue.
// Without nextTick, update:modelValue overwrites the auto-fill data with stale props.itemData.
function handleContactChange(payload: { sectionId: string; index: number; value: any }) {
  const { sectionId, index, value } = payload;
  if (!value) return;

  let contact: Contact | null = null;
  if (typeof value === 'object' && value !== null) {
    contact = value as Contact;
  } else if (typeof value === 'string') {
    contact = contacts.value.find(c => c.contactName === value.trim()) || null;
  }
  if (!contact) return;

  nextTick(() => {
    const sectionRef = sectionRefs[sectionId];
    if (!sectionRef) return;
    const items = sectionRef.getDynamicItems?.() || [];
    if (index >= items.length) return;

    const item = { ...items[index] };
    item.name = contact!.contactName.toUpperCase();

    if (sectionId === 'shareholders') {
      item.type = determineShareholderType(contact!);
    }

    if (contact!.contactRegNo) {
      if (item.type === 'BodyCorporate') {
        item.compRegNo = contact!.contactRegNo.toUpperCase();
        item.corporateName = contact!.contactName.toUpperCase();
      } else {
        item.idNumber = contact!.contactRegNo.toUpperCase();
      }
    }

    const mappedIdType = mapContactIdType(contact!);
    if (mappedIdType && item.type !== 'BodyCorporate') item.idType = mappedIdType;
    if (contact!.contactEmail) item.email = contact!.contactEmail;

    if (contact!.dateOfBirth && item.type !== 'BodyCorporate') {
      try {
        const birthDate = new Date(contact!.dateOfBirth);
        if (!isNaN(birthDate.getTime())) item.dob = formatDateToDisplay(birthDate);
      } catch (e) { /* ignore */ }
    }

    items[index] = item;
    sectionRef.setDynamicItems?.(items);

    if (sectionId === 'directors') directors.value = items.map((d: any) => ({ ...d }));
    else if (sectionId === 'shareholders') shareholders.value = items.map((s: any) => ({ ...s }));
    emitFormData();
  });
}

// Date picker handlers
function handleDatePickerChange(payload: { sectionId: string; index: number; value: any }) {
  const { sectionId, index, value } = payload;
  if (!value) return;
  const formatted = formatDateToDisplay(new Date(value));
  const sectionRef = sectionRefs[sectionId];
  if (!sectionRef) return;
  const items = sectionRef.getDynamicItems?.() || [];
  if (index >= items.length) return;
  items[index] = { ...items[index], dob: formatted };
  sectionRef.setDynamicItems?.(items);
  if (sectionId === 'directors') directors.value = items.map((d: any) => ({ ...d }));
  else if (sectionId === 'shareholders') shareholders.value = items.map((s: any) => ({ ...s }));
  emitFormData();
}

function handleDateBlur(payload: { sectionId: string; index: number }) {
  const { sectionId, index } = payload;
  const sectionRef = sectionRefs[sectionId];
  if (!sectionRef) return;
  const items = sectionRef.getDynamicItems?.() || [];
  if (index >= items.length) return;
  const val = items[index].dob;
  if (!val || /^\d{1,2}\s+[A-Z]+\s+\d{4}$/.test(val)) return;
  const date = parseDateString(val);
  if (date && !isNaN(date.getTime())) {
    items[index] = { ...items[index], dob: formatDateToDisplay(date) };
    sectionRef.setDynamicItems?.(items);
    if (sectionId === 'directors') directors.value = items.map((d: any) => ({ ...d }));
    else if (sectionId === 'shareholders') shareholders.value = items.map((s: any) => ({ ...s }));
    emitFormData();
  }
}

// Reservation select handler
function handleReservationSelectChange(value: string) {
  const item = (props.reservationList || []).find(r => r.entity_name === value);
  if (item) {
    formData['reservedName.reservedEntityName'] = item.entity_name;
    formData['reservedName.reservedEntityNumber'] = item.reservation_number || '';
    emit('reservation-select', item);

    // 예약에 등록된 디렉터 정보가 있으면 Directors 섹션에 자동 채우기
    if (item.directors?.length) {
      populateDirectorsFromReservation(item.directors);
    }
  }
}

// SSM ID type → form idType 매핑
const ssmIdTypeMapping: Record<string, string> = {
  'NRIC': 'NRIC',
  'MALAYSIAN NRIC': 'NRIC',
  'PASSPORT': 'PASSPORT',
  'ARMY ID': 'ARMY_ID',
  'POLICE ID': 'POLICE_ID',
  'MYKAS': 'MYKAS',
  'PERMANENT RESIDENCE NUMBER': 'PR_NUMBER',
};

// SSM date format ("11 July 1983") → display format ("11 JULY 1983")
function normalizeSsmDate(dateStr: string): string {
  if (!dateStr) return '';
  return dateStr.toUpperCase().trim();
}

// SSM select 값 → 프론트엔드 select value 정규화
// SSM: "KOREA SOUTH" → Frontend: "KOREA_SOUTH"
function normalizeSsmSelectValue(val: string, refOptions: RefDataItem[]): string {
  if (!val) return '';
  const upper = val.toUpperCase().trim();
  // 1) 직접 매치
  const exact = refOptions.find(o => o.value === upper);
  if (exact?.value) return exact.value;
  // 2) space→underscore 변환 매치
  const underscored = upper.replace(/\s+/g, '_');
  const match = refOptions.find(o => o.value === underscored);
  if (match?.value) return match.value;
  // 3) label로 매치 (SSM이 label 텍스트를 반환하는 경우)
  const byLabel = refOptions.find(o => o.label?.toUpperCase() === upper);
  if (byLabel?.value) return byLabel.value;
  return '';
}

function populateDirectorsFromReservation(reservationDirectors: ReservationDirectorItem[]) {
  const nationalities = refData.nationalities || [];
  const genders = refData.genders || [];
  const races = refData.races || [];

  const newDirectors: Record<string, any>[] = reservationDirectors.map(dir => {
    const idTypeUpper = (dir.identification_type || '').toUpperCase().trim();
    const mappedIdType = ssmIdTypeMapping[idTypeUpper] || idTypeUpper;

    const item: Record<string, any> = {
      type: 'Individual',
      idType: mappedIdType,
      idNumber: (dir.identification_number || '').toUpperCase(),
      name: (dir.person_name || '').toUpperCase(),
      dob: normalizeSsmDate(dir.date_of_birth || ''),
      nationality: normalizeSsmSelectValue(dir.nationality || '', nationalities),
      gender: normalizeSsmSelectValue(dir.gender || '', genders),
      race: normalizeSsmSelectValue(dir.race || '', races),
      email: dir.email || '',
      mobile: dir.mobile_number || '',
      office: dir.office_number || '',
      isAlsoMember: false,
      _fromReservation: true,
    };

    // 주소 정보가 있으면 매핑
    if (dir.residential_address) {
      item._address = {
        country: dir.residential_address.country || 'MALAYSIA',
        state: dir.residential_address.state || '',
        city: dir.residential_address.city || '',
        postcode: dir.residential_address.post_code || '',
        street01: dir.residential_address.street01 || '',
        street02: dir.residential_address.street02 || '',
        street03: dir.residential_address.street03 || '',
      };
    }

    return item;
  });

  directors.value = newDirectors;
  nextTick(() => {
    sectionRefs['directors']?.setDynamicItems?.(directors.value);
    emitFormData();
  });
}

// Field/address/list handlers
function handleFieldUpdate(payload: { sectionId: string; fieldId: string; value: any }) {
  formData[`${payload.sectionId}.${payload.fieldId}`] = payload.value;
  emitFormData();
}

function handleAddressUpdate(payload: { sectionId: string; addressId: string; value: Record<string, any> }) {
  formData[`${payload.sectionId}._addr.${payload.addressId}`] = payload.value;
  emitFormData();
}

function handleListUpdate(payload: { sectionId: string; items: any[] }) {
  if (payload.sectionId === 'businessCodes') businessCodes.value = payload.items;
  emitFormData();
}

function handleDynamicListUpdate(payload: { sectionId: string; items: any[] }) {
  if (payload.sectionId === 'directors') directors.value = payload.items;
  else if (payload.sectionId === 'shareholders') shareholders.value = payload.items;
  emitFormData();
}

// Director→Shareholder sync
function handleDirectorMemberToggle(payload: { index: number; isAlsoMember: boolean; directorData: Record<string, any> }) {
  const director = payload.directorData;
  if (payload.isAlsoMember) {
    const existing = shareholders.value.find(s => s._syncedFromDirector === payload.index);
    if (!existing) {
      const synced: Record<string, any> = {
        type: 'Individual',
        _syncedFromDirector: payload.index,
        idType: director.idType,
        idNumber: director.idNumber,
        name: director.name,
        dob: director.dob,
        nationality: director.nationality,
        gender: director.gender,
        race: director.race,
        email: director.email,
        pricePerShare: 1,
        numberOfShares: 1,
        classOfShare: '',
        _address: director._address ? { ...director._address } : {},
      };
      shareholders.value.push(synced);
      const shRef = sectionRefs['shareholders'];
      if (shRef?.setDynamicItems) shRef.setDynamicItems(shareholders.value);
    }
  } else {
    const idx = shareholders.value.findIndex(s => s._syncedFromDirector === payload.index);
    if (idx >= 0) {
      shareholders.value.splice(idx, 1);
      const shRef = sectionRefs['shareholders'];
      if (shRef?.setDynamicItems) shRef.setDynamicItems(shareholders.value);
    }
  }
  emitFormData();
}

function emitFormData() {
  emit('update:formData', getFormData());
}

// Section icons
function getSectionIcon(section: SectionDef): string {
  const icons: Record<string, string> = {
    reservedName: 'mdi-bookmark-check',
    businessCodes: 'mdi-tag-multiple',
    applicationDetails: 'mdi-file-document-outline',
    directors: 'mdi-account-tie',
    shareholders: 'mdi-account-group',
    informationToAgency: 'mdi-office-building',
    declaration: 'mdi-check-decagram',
  };
  return icons[section.id] || 'mdi-circle-outline';
}

// ===== Exposed methods =====

async function validate(): Promise<boolean> {
  showAddressErrors.value = true;
  const errors: Record<string, string[]> = {};
  let hasError = false;
  const postcodes = refData.postcodes || [];

  function isValidPostcode(value: string): boolean {
    if (!value) return false;
    if (postcodes.length === 0) return true;
    return postcodes.some((p: any) => p.value === value);
  }

  // Validate reserved name selection
  if (!selectedEntityName.value) {
    errors['reservedName'] = [tl('Reserved Entity Name is required')];
    hasError = true;
  }

  for (const section of sections) {
    const msgs: string[] = [];

    // reservedName은 위에서 별도 검증
    if (section.id === 'reservedName') continue;

    if (section.fields) {
      for (const field of section.fields) {
        if (field.required && !field.readonly) {
          const key = `${section.id}.${field.id}`;
          const val = formData[key];
          if (!val && val !== 0 && val !== false) {
            msgs.push(`${tl(field.label || field.id)} ${tl('is required')}`);
          }
        }
      }
    }

    if (section.addressBlocks) {
      for (const ab of section.addressBlocks) {
        const prefix = ab.title ? `${tl(ab.title)} - ` : '';
        const addr = formData[`${section.id}._addr.${ab.id}`] || {};
        if (!addr.country) msgs.push(`${prefix}${tl('Country is required')}`);
        const addrIsMalaysia = (addr.country || 'MALAYSIA') === 'MALAYSIA';
        if (addrIsMalaysia) {
          if (!addr.state) msgs.push(`${prefix}${tl('State is required')}`);
          if (!addr.city) msgs.push(`${prefix}${tl('City is required')}`);
          if (!addr.postcode) msgs.push(`${prefix}${tl('Post Code is required')}`);
          else if (!isValidPostcode(addr.postcode)) msgs.push(`${prefix}${tl('Post Code is invalid')}`);
        }
        if (!addr.street01) msgs.push(`${prefix}${tl('Street is required')}`);
      }
    }

    if (section.type === 'dynamic-list') {
      const items = section.id === 'directors' ? directors.value : shareholders.value;
      const label = section.id === 'directors' ? tl('Director') : tl('Shareholder');
      if (items.length < (section.minItems || 1)) {
        msgs.push(`${tl('At least')} ${section.minItems || 1} ${label} ${tl('is required')}`);
      }
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const tag = item.name ? item.name : `${label} #${i + 1}`;
        if (!item.idType) msgs.push(`${tag}: ${tl('ID Type is required')}`);
        if (!item.idNumber) msgs.push(`${tag}: ${tl('ID Number is required')}`);
        if (!item.name) msgs.push(`${tag}: ${tl('Name is required')}`);
        // Individual-specific required fields
        if (item.type !== 'BodyCorporate') {
          if (!item.dob) msgs.push(`${tag}: ${tl('Date of Birth is required')}`);
          if (!item.nationality) msgs.push(`${tag}: ${tl('Nationality is required')}`);
          if (!item.gender) msgs.push(`${tag}: ${tl('Gender is required')}`);
          if (!item.race) msgs.push(`${tag}: ${tl('Race is required')}`);
          if (!item.email) msgs.push(`${tag}: ${tl('Email is required')}`);
        }
        const addr = item._address || {};
        const itemAddrIsMalaysia = (addr.country || 'MALAYSIA') === 'MALAYSIA';
        if (itemAddrIsMalaysia) {
          if (!addr.state) msgs.push(`${tag}: ${tl('State is required')}`);
          if (!addr.city) msgs.push(`${tag}: ${tl('City is required')}`);
          if (!addr.postcode) msgs.push(`${tag}: ${tl('Post Code is required')}`);
          else if (!isValidPostcode(addr.postcode)) msgs.push(`${tag}: ${tl('Post Code is invalid')}`);
        }
        if (!addr.street01) msgs.push(`${tag}: ${tl('Street is required')}`);
      }
    }

    if (section.type === 'simple-list') {
      if (businessCodes.value.filter(Boolean).length < (section.minItems || 1)) {
        msgs.push(`${tl('At least')} ${section.minItems || 1} ${tl('Business Code is required')}`);
      }
    }

    if (section.type === 'declaration' && section.required) {
      const val = formData[`${section.id}.declaration`] || formData['declaration'];
      if (!val) msgs.push(tl('Declaration must be checked'));
    }

    if (msgs.length > 0) {
      errors[section.id] = msgs;
      hasError = true;
    }
  }

  sectionErrors.value = errors;
  if (hasError) {
    const errorSections = Object.keys(errors);
    openPanels.value = [...new Set([...openPanels.value, ...errorSections])];
  }
  return !hasError;
}

function getFormData(): any {
  const result: Record<string, any> = {};

  for (const section of sections) {
    if (section.type === 'standard' || section.type === 'declaration') {
      const sectionData: Record<string, any> = {};
      if (section.fields) {
        for (const field of section.fields) {
          sectionData[field.id] = formData[`${section.id}.${field.id}`] ?? '';
        }
      }
      if (section.conditionalGroups) {
        for (const cg of section.conditionalGroups) {
          for (const field of cg.fields) {
            sectionData[field.id] = formData[`${section.id}.${field.id}`] ?? '';
          }
        }
      }
      if (section.extraGroups) {
        for (const eg of section.extraGroups) {
          for (const field of eg.fields) {
            sectionData[field.id] = formData[`${section.id}.${field.id}`] ?? '';
          }
        }
      }
      if (section.addressBlocks) {
        sectionData._addresses = {};
        for (const ab of section.addressBlocks) {
          sectionData._addresses[ab.id] = formData[`${section.id}._addr.${ab.id}`] || {};
        }
      }
      if (section.type === 'declaration') {
        sectionData.declaration = formData[`${section.id}.declaration`] || false;
      }
      result[section.id] = sectionData;
    }
  }

  result.businessCodes = businessCodes.value.filter(Boolean);
  result.directors = directors.value.map(d => ({ ...d }));
  result.shareholders = shareholders.value.map(s => ({ ...s }));

  const infoRef = sectionRefs['informationToAgency'];
  if (infoRef?.getBranchAddresses) {
    result.branchAddresses = infoRef.getBranchAddresses();
  }

  return result;
}

function resetForm() {
  Object.keys(formData).forEach(key => delete formData[key]);
  directors.value = [];
  shareholders.value = [];
  businessCodes.value = [''];
  sectionErrors.value = {};
  openPanels.value = sections.map(s => s.id);
}

function loadSavedData(data: any) {
  if (!data) return;

  // Restore reserved name selection
  if (data.reservedName?.reservedEntityName) {
    selectedEntityName.value = data.reservedName.reservedEntityName;
    formData['reservedName.reservedEntityName'] = data.reservedName.reservedEntityName;
    formData['reservedName.reservedEntityNumber'] = data.reservedName.reservedEntityNumber || '';
  }

  for (const section of sections) {
    const sectionData = data[section.id];
    if (!sectionData) continue;

    if (section.fields) {
      for (const field of section.fields) {
        if (sectionData[field.id] !== undefined) {
          formData[`${section.id}.${field.id}`] = sectionData[field.id];
        }
      }
    }
    if (section.conditionalGroups) {
      for (const cg of section.conditionalGroups) {
        for (const field of cg.fields) {
          if (sectionData[field.id] !== undefined) formData[`${section.id}.${field.id}`] = sectionData[field.id];
        }
      }
    }
    if (section.extraGroups) {
      for (const eg of section.extraGroups) {
        for (const field of eg.fields) {
          if (sectionData[field.id] !== undefined) formData[`${section.id}.${field.id}`] = sectionData[field.id];
        }
      }
    }
    if (sectionData._addresses) {
      for (const [addrId, addrData] of Object.entries(sectionData._addresses)) {
        formData[`${section.id}._addr.${addrId}`] = addrData;
      }
    }
    if (section.type === 'declaration' && sectionData.declaration !== undefined) {
      formData[`${section.id}.declaration`] = sectionData.declaration;
    }
  }

  if (data.businessCodes) {
    businessCodes.value = [...data.businessCodes];
    const bcRef = sectionRefs['businessCodes'];
    if (bcRef?.setListItems) bcRef.setListItems(data.businessCodes);
  }
  if (data.directors) {
    directors.value = data.directors.map((d: any) => ({ ...d }));
    nextTick(() => { sectionRefs['directors']?.setDynamicItems?.(directors.value); });
  }
  if (data.shareholders) {
    shareholders.value = data.shareholders.map((s: any) => ({ ...s }));
    nextTick(() => { sectionRefs['shareholders']?.setDynamicItems?.(shareholders.value); });
  }
  if (data.branchAddresses) {
    nextTick(() => { sectionRefs['informationToAgency']?.setBranchAddresses?.(data.branchAddresses); });
  }
}

function getValidationErrors(): string[] {
  const errors: string[] = [];
  for (const [sectionId, msgs] of Object.entries(sectionErrors.value)) {
    const section = sections.find(s => s.id === sectionId);
    const title = tl(section?.title || sectionId);
    errors.push(`${title}: ${msgs.length} ${tl('error(s)')}`);
    for (const msg of msgs) errors.push(`  - ${msg}`);
  }
  return errors;
}

defineExpose({
  validate,
  getFormData,
  resetForm,
  loadSavedData,
  getValidationErrors,
});
</script>

<style scoped>
.ir-form {
  max-width: 100%;
}

:deep(.v-expansion-panel) {
  margin-bottom: 8px;
}

:deep(.v-expansion-panel-title) {
  min-height: 48px;
}

:deep(.v-expansion-panel-title--active) {
  min-height: 48px;
}
</style>
